import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Save } from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getPlatformConfig,
  listTenantCompanies,
  setSubscriptionIssuer,
  setSubscriptionIssuerBrand,
} from "@/services/platformConfigService";

const QK = {
  config: ["platform-config"],
  tenantCompanies: ["platform-config", "tenant-companies"],
};

export function PlatformConfigPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pendingIssuerId, setPendingIssuerId] = useState<string | null>(null);

  const configQuery = useQuery({
    queryKey: QK.config,
    queryFn: getPlatformConfig,
  });

  const companiesQuery = useQuery({
    queryKey: QK.tenantCompanies,
    queryFn: listTenantCompanies,
  });

  /** Sentinel: Radix Select rejects value="". */
  const NO_BRAND = "__none__";
  const issuerBrands = configQuery.data?.issuerBrands ?? [];
  const configuredBrand = configQuery.data?.subscriptionIssuerBrand ?? null;
  /**
   * A configured marca that is no longer in the active list — archived, or left
   * over from a previous issuer. Appended as an option so the Select renders a
   * name instead of a blank, and so the operator can actually clear it. The
   * emission path already refuses to use it.
   */
  const staleBrand =
    configuredBrand && !issuerBrands.some((b) => b.id === configuredBrand.id)
      ? configuredBrand
      : null;
  const setBrandMutation = useMutation({
    mutationFn: (brandId: string | null) => setSubscriptionIssuerBrand(brandId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QK.config });
      toast.success(
        t("platformConfig.issuerBrand.saved", "Marca de suscripciones actualizada."),
      );
    },
    onError: () =>
      toast.error(
        t("platformConfig.issuerBrand.error", "No pudimos guardar la marca."),
      ),
  });

  const setIssuerMutation = useMutation({
    mutationFn: (companyId: string | null) => setSubscriptionIssuer(companyId),
    onSuccess: () => {
      toast.success(
        t("platformConfig.saved", "Configuración actualizada."),
      );
      setPendingIssuerId(null);
      void queryClient.invalidateQueries({ queryKey: QK.config });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : t("platformConfig.saveError", "No se pudo guardar.");
      toast.error(message);
    },
  });

  const currentIssuerId = configQuery.data?.subscriptionIssuerCompanyId ?? null;
  const currentIssuer = configQuery.data?.subscriptionIssuerCompany ?? null;
  const selectedIssuerId = pendingIssuerId ?? currentIssuerId ?? "";
  const isDirty =
    pendingIssuerId !== null && pendingIssuerId !== currentIssuerId;

  if (configQuery.isLoading || companiesQuery.isLoading) {
    return <LoadingLogo />;
  }

  return (
    <div className="fx-content space-y-6">
      <PageHeader
        title={t("platformConfig.title", "Configuración de la plataforma")}
        description={t(
          "platformConfig.description",
          "Ajustes globales editables por administradores root.",
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t(
              "platformConfig.subscriptionIssuer.title",
              "Emisor de comprobantes de suscripción",
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "platformConfig.subscriptionIssuer.description",
              "Tenant que emite los CFE de las suscripciones mensuales/anuales cobradas por Fiscalo. Cuando se registre Fiscalo SAS como tenant, cambialo acá.",
            )}
          </p>

          {currentIssuer ? (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <p className="font-medium text-foreground">
                {currentIssuer.name}
                {currentIssuer.legalName &&
                  currentIssuer.legalName !== currentIssuer.name && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({currentIssuer.legalName})
                    </span>
                  )}
              </p>
              <p className="text-xs text-muted-foreground">
                RUT {currentIssuer.rut}
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t(
                "platformConfig.subscriptionIssuer.none",
                "Ningún tenant seleccionado. Las suscripciones no van a emitir comprobantes hasta que configures uno.",
              )}
            </div>
          )}

          {/* The marca those CFEs present. Separate from the issuer's own
              default marca on purpose: that default serves SALKANTAI's
              commercial invoicing, and changing it must not silently re-brand
              every Fiscalo subscription invoice. Only shown once an issuer is
              set, since the brands listed are that issuer's. */}
          {(issuerBrands.length > 0 || staleBrand) && (
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {t(
                  "platformConfig.issuerBrand.selectLabel",
                  "Marca de las facturas de suscripción",
                )}
              </label>
              <Select
                value={configQuery.data?.subscriptionIssuerBrandId ?? NO_BRAND}
                onValueChange={(value) =>
                  setBrandMutation.mutate(value === NO_BRAND ? null : value)
                }
                disabled={setBrandMutation.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_BRAND}>
                    {t(
                      "platformConfig.issuerBrand.none",
                      "Sin marca (razón social del emisor)",
                    )}
                  </SelectItem>
                  {issuerBrands.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                  {staleBrand && (
                    <SelectItem value={staleBrand.id}>
                      {t(
                        "platformConfig.issuerBrand.unavailable",
                        "{{name}} — no disponible, elegí otra",
                        { name: staleBrand.name }
                      )}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {t(
                  "platformConfig.subscriptionIssuer.selectLabel",
                  "Cambiar tenant emisor",
                )}
              </label>
              <Select
                value={selectedIssuerId}
                onValueChange={(value) =>
                  setPendingIssuerId(value === "" ? null : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={t(
                      "platformConfig.subscriptionIssuer.selectPlaceholder",
                      "Elegí un tenant",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(companiesQuery.data ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — RUT {c.rut}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() =>
                setIssuerMutation.mutate(pendingIssuerId ?? currentIssuerId)
              }
              disabled={!isDirty || setIssuerMutation.isPending}
            >
              {setIssuerMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t("platformConfig.save", "Guardar")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
